import { useEffect, useSyncExternalStore } from 'react'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { LogoCompacta } from '@/ui/marca/Logo'
import { sesionEmpleado, suscribirseSesionEmpleado } from '@/dominio/empleado/sesion'
import { clavesEmpleado, usePerfilEmpleado } from '@/dominio/empleado/usePortal'

/**
 * El portal del empleado (`docs/10-nomina.md` §2.9).
 *
 * **Su propio armazón, sin el del portal de la empresa.** El empleado no tiene
 * cuenta en la plataforma: entra con el enlace que le manda su empresa y su
 * documento, y no ve el menú de facturación ni el de la nómina. La raíz
 * (`__root.tsx`) no le pide la sesión del portal en `/empleado`, igual que no
 * se la pide a la consola del dueño.
 *
 * **Se abre en el teléfono.** Una columna, controles del alto de un dedo, y el
 * nombre de la empresa arriba para que sepa de quién es lo que ve.
 */
export const Route = createFileRoute('/empleado')({
  component: ArmazonEmpleado,
})

function ArmazonEmpleado() {
  const sesion = useSyncExternalStore(suscribirseSesionEmpleado, sesionEmpleado, () => null)
  const perfil = usePerfilEmpleado(sesion !== null)
  const cliente = useQueryClient()

  // Al cerrar la sesión o al caducar, lo cacheado era de él: no se deja en memoria.
  useEffect(() => {
    if (sesion === null) cliente.removeQueries({ queryKey: clavesEmpleado.todo })
  }, [sesion, cliente])

  useEffect(() => {
    const anterior = document.title
    document.title = 'Portal del empleado'
    return () => {
      document.title = anterior
    }
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-gris-100">
      <header className="border-b border-gris-300 bg-white">
        <div className="mx-auto flex w-full max-w-[720px] items-center justify-between gap-3 px-4 py-3">
          <LogoCompacta className="h-5 w-auto shrink-0 text-gris-900" />
          <span className="min-w-0 truncate text-etiqueta text-gris-600">
            {sesion !== null && perfil.data ? perfil.data.razonSocial : 'Portal del empleado'}
          </span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-[720px] flex-1 flex-col gap-4 px-4 py-4">
        <Outlet />
      </main>

      <footer className="mx-auto w-full max-w-[720px] px-4 pb-6 text-etiqueta text-gris-500">
        Agentical Nómina · Sus datos los administra su empresa; para corregir algo, pídaselo desde «Mis datos» o hable con ella.
      </footer>
    </div>
  )
}
